/**
 * mapCachePersister — lightweight React Query persistence for ['maps', ...]
 * cache keys across cold launches.
 *
 * SCOPE: only ['maps', …] keys are persisted. Game state, user data, and
 * auth tokens are NEVER touched by this module.
 *
 * IMPLEMENTATION CHOICE
 * ─────────────────────
 * The doc (`docs/map-perf.md` Tier 1 #2) recommends MMKV +
 * @tanstack/react-query-persist-client. Neither package is currently in the
 * dependency tree:
 *   - react-native-mmkv: requires a new native build (owner is rationing EAS
 *     builds). Adding it is deferred until the next scheduled build window.
 *   - @react-native-async-storage/async-storage: also absent, also requires
 *     a native build.
 *   - expo-file-system: absent from package.json.
 *   - expo-secure-store: present but has a 2 048-byte value limit — far too
 *     small for a MapDef (world-classic.json is ~200 KB).
 *
 * CURRENT APPROACH
 * ─────────────────
 * On web (warcarp.com): use localStorage, which is synchronous and available
 * from the first render. This gives the full cold-launch-instant experience
 * on the web deployment.
 *
 * On native (iOS / Android): a no-op stub that always reads `null`. The
 * bundled-asset seeding (Tier 1 #1 in useMaps.ts) already covers the
 * cold-launch gap on native — the JSON is in the app bundle so no network
 * round-trip is needed. A proper MMKV persister can be layered on top once
 * the next native build window opens; the interface is the same so no
 * callers need to change.
 *
 * USAGE
 * ──────
 * Called from lib/queryClient.ts after the QueryClient is created:
 *
 *   import { rehydrateMapsCache, persistMapsCache } from './mapCachePersister'
 *   rehydrateMapsCache(queryClient)       // run once on startup
 *   // listen for cache changes and persist:
 *   queryClient.getQueryCache().subscribe(() => persistMapsCache(queryClient))
 *
 * NOTE: @tanstack/react-query-persist-client would handle this more robustly
 * (buster keys, max age, serialisation options). When MMKV is added, migrate
 * to that package with a `createSyncStoragePersister` wrapping MMKV.
 */

import { Platform } from 'react-native'
import Constants from 'expo-constants'
import type { QueryClient } from '@tanstack/react-query'

const STORAGE_KEY = 'warcarp_maps_cache_v1'

/**
 * Cache buster. MapDefs are persisted with `staleTime: Infinity`, so without
 * this an old persisted map (e.g. a since-merged territory or a since-fixed
 * border) would be rehydrated FOREVER and never re-fetched — existing users
 * would stay stuck on the old map after any map update. We key the persisted
 * snapshot to the app version: every map change ships a version bump (per the
 * release workflow), so a version mismatch discards the stale snapshot and the
 * next launch re-seeds from the (fresh) bundled assets / network.
 */
const APP_VERSION = Constants.expoConfig?.version ?? '0.0.0'

/** True when localStorage is available and usable (web only). */
const CAN_PERSIST = Platform.OS === 'web' && typeof localStorage !== 'undefined'

/**
 * Whether a persisted snapshot's buster still matches the running app version.
 * Pure + exported for testing. A `undefined` buster (legacy snapshot written
 * before busting existed) never matches, so it's correctly discarded.
 */
export function persistedCacheIsCurrent(
  persistedBuster: string | undefined,
  appVersion: string,
): boolean {
  return persistedBuster !== undefined && persistedBuster === appVersion
}

/** Serialised cache shape written to / read from storage. */
interface PersistedMapsCache {
  /** epoch-ms when this snapshot was written */
  savedAt: number
  /** App version that wrote this snapshot — the cache buster (see APP_VERSION). */
  buster: string
  /**
   * Array of [queryKey, data] pairs for every ['maps', …] cache entry.
   * queryKey is JSON-stringified so it survives serialisation.
   */
  entries: Array<{ key: string; data: unknown }>
}

/**
 * Determine whether a query key belongs to the maps cache family.
 * Only ['maps', …] keys are persisted.
 */
function isMapsKey(queryKey: unknown): boolean {
  return Array.isArray(queryKey) && queryKey[0] === 'maps'
}

/**
 * Write all ['maps', …] cache entries to storage.
 *
 * Called by the QueryCache subscriber in queryClient.ts whenever the cache
 * changes. Runs synchronously on web (localStorage); no-ops on native.
 *
 * This is intentionally a fire-and-forget write — if localStorage throws
 * (quota exceeded, private browsing) we catch and ignore.
 */
export function persistMapsCache(qc: QueryClient): void {
  if (!CAN_PERSIST) return

  try {
    const queries = qc.getQueryCache().findAll({ queryKey: ['maps'] })
    const entries: PersistedMapsCache['entries'] = []

    for (const query of queries) {
      if (!isMapsKey(query.queryKey)) continue
      const data = query.state.data
      if (data === undefined) continue
      entries.push({ key: JSON.stringify(query.queryKey), data })
    }

    if (entries.length === 0) {
      localStorage.removeItem(STORAGE_KEY)
      return
    }

    const payload: PersistedMapsCache = { savedAt: Date.now(), buster: APP_VERSION, entries }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Quota exceeded or storage unavailable — swallow silently.
    // The next successful write will clear the stale entry.
  }
}

/**
 * Rehydrate the React Query cache from the persisted snapshot on startup.
 *
 * Uses `setQueryData` with no options so the seeded entry is treated as
 * already-loaded data. Combined with `staleTime: Infinity`, this means the
 * rehydrated data is never re-fetched — the session uses the cached copy.
 * The network fetch from `useMap()` / `useMaps()` still runs on first mount
 * (the `queryFn` is called because `status` starts as `loading` until the
 * first successful response is cached), but the UI renders immediately from
 * the rehydrated data.
 *
 * No-ops on native (returns immediately).
 */
export function rehydrateMapsCache(qc: QueryClient): void {
  if (!CAN_PERSIST) return

  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return

    const payload = JSON.parse(raw) as PersistedMapsCache
    if (!payload || !Array.isArray(payload.entries)) return

    // Discard a snapshot written by a different app version — its maps may be
    // stale (since-merged territories, re-fixed borders, suppressSeaRoutes,
    // etc.). Dropping it forces a fresh re-seed from the bundled assets/network.
    if (!persistedCacheIsCurrent(payload.buster, APP_VERSION)) {
      try { localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
      return
    }

    // In DEV, don't rehydrate at all. Populating the cache makes the entry
    // permanently fresh (useMap/useMaps run at staleTime: Infinity), so the
    // fetch never runs and an edited map keeps rendering its old geometry —
    // the same trap that the bundled-asset seeding used to cause on native
    // (see hooks/useMaps.ts). In dev you are editing maps, so correctness beats
    // the few ms of instant paint. Production keeps the fast path, guarded by
    // the APP_VERSION buster above.
    if (typeof __DEV__ !== 'undefined' && __DEV__) return

    for (const entry of payload.entries) {
      const qk = JSON.parse(entry.key) as unknown[]
      // Only load if not already populated.
      if (qc.getQueryData(qk) === undefined) {
        qc.setQueryData(qk, entry.data)
      }
    }
  } catch {
    // Corrupt or missing storage entry — start fresh.
    try { localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
  }
}
